import { Button } from '@/components/ui/Button'
import type { FormEvent, KeyboardEvent, MouseEvent, MutableRefObject } from 'react'
import { useEffect, useState } from 'react'
// import errorIcon from '../../assets/icons/ico_error_text.svg'
import inputStyles from '../../styles/input.module.css'
import styles from './forgotPassword.module.css'

// type EnterOTPFormErrors = {
//   general?: string
//   [key: string]: string | undefined
// }

interface EnterOTPFormProps {
  otpValues: string[]
  // errors: EnterOTPFormErrors
  // generalError: string | undefined
  isLoading: boolean
  inputRefs: MutableRefObject<(HTMLInputElement | null)[]>
  handleOTPChange: (index: number, value: string) => void
  handleKeyDown: (index: number, e: KeyboardEvent<HTMLInputElement>) => void
  handleSubmit: (e: FormEvent) => void
  onLinkClick: (page: string) => void
  onBack: () => void
  handleResendOTP: () => void
  isSubmitting: boolean
}

const EnterOTPForm = ({
  otpValues,
  // generalError,
  isLoading,
  inputRefs,
  handleOTPChange,
  handleKeyDown,
  handleSubmit,
  onBack,
  isSubmitting,
  handleResendOTP,
}: EnterOTPFormProps) => {
  const [countdown, setCountdown] = useState(30)

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1)
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [countdown])

  const handleKeyDownButton = (event: KeyboardEvent, handler: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handler()
    }
  }

  const handleResendClick = () => {
    handleResendOTP()
    setCountdown(30)
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const areAllFieldsFilled = !!otpValues[0] && !!otpValues[1] && !!otpValues[2] && !!otpValues[3]
  const nextDisabled = isSubmitting || !areAllFieldsFilled

  return (
    <form
      className={styles.form}
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        handleSubmit(e)
      }}
    >
      {/* <div className={inputStyles.errorTextWrapper}>
        {generalError && <img src={errorIcon} alt="error" />}
        <p className={inputStyles.errorText}>{generalError}</p>
      </div> */}

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '10px',
          }}
        >
          {otpValues.map((value, index) => {
            const inputId = `otp-input-${index}`

            return (
              <input
                key={inputId}
                id={inputId}
                ref={(el) => {
                  if (inputRefs.current) {
                    inputRefs.current[index] = el
                  }
                }}
                className={inputStyles.input}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={value}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                style={{
                  textAlign: 'center',
                  fontSize: '1.2em',
                  fontWeight: '500',
                  width: '50px',
                  height: '50px',
                  minWidth: 'unset',
                }}
                placeholder="0"
                aria-label={`Digit ${index + 1} of verification code`}
              />
            )
          })}
        </div>
      </fieldset>

      <div className={styles.additional}>
        <p className={styles.text}>Didn't receive a code?</p>
        <button
          type="button"
          className={[styles.link, styles.centered].join(' ')}
          onClick={handleResendClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleResendClick()
            }
          }}
          disabled={countdown > 0}
          style={{ cursor: countdown > 0 ? 'not-allowed' : 'pointer', padding: 0 }}
        >
          {countdown > 0 ? `Request again in ${formatTime(countdown)}` : 'Request'}
        </button>
      </div>

      <div className={styles.buttonGroup}>
        <Button
          type="button"
          variant="outline"
          size="large"
          style={{ width: '100%' }}
          className={styles.backBtn}
          onClick={onBack}
          onKeyDown={(e) => handleKeyDownButton(e, onBack)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="fill"
          size="large"
          style={{ width: '100%' }}
          className={`${styles.submitBtn} ${nextDisabled ? styles.submitBtnDisabled : ''}`}
          onClick={(e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(e as unknown as FormEvent)
          }}
          onKeyDown={(e) =>
            handleKeyDownButton(e, () => {
              e.preventDefault()
              handleSubmit(e as unknown as FormEvent)
            })
          }
          disabled={nextDisabled}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </Button>
      </div>
    </form>
  )
}

export default EnterOTPForm
